/* Copyright (c) 2020-present CloudIO Inc */

import {
  atom,
  useStateXAtomSetter,
  useStateXAtomValue,
} from '@cloudio-saas/statex';
import { createTheme, Theme, ThemeOptions } from '@mui/material';
import { SimplePaletteColorOptions } from '@mui/material/styles/createPalette';
import { useAppID } from '../designer/atoms';
import { useEffect, useLayoutEffect } from 'react';
import useUser from '../root/useUser';
import {
  primaryPaletteColorOptions,
  secondaryPaletteColorOptions,
  successPaletteColorOptions,
  useBaseTheme,
  warningPaletteColorOptions,
} from './DefaultTheme';
import {
  darkModeAtom,
  useDirection,
  userDarkModeAtom,
  applicationDarkModeAtom,
} from '../utils/state';
import tinycolor from 'tinycolor2';
import { usePlatform } from '../platform/PlatformHooks';

export const primaryColorAtom = atom<SimplePaletteColorOptions>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'primaryColor'],
  defaultValue: primaryPaletteColorOptions,
});

export const fontFamilyAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'fontFamily'],
  defaultValue: '"Roboto","Helvetica","Arial","sans-serif"',
});

export const fontSizeAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'fontSize'],
  defaultValue: 14,
});

export const headerHeightAtom = atom({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'headerHeight'],
  defaultValue: 60,
});

export const secondaryColorAtom = atom<SimplePaletteColorOptions>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'secondaryColor'],
  defaultValue: secondaryPaletteColorOptions,
});

export const warningColorAtom = atom<SimplePaletteColorOptions>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'warningColor'],
  defaultValue: warningPaletteColorOptions,
});

export const successColorAtom = atom<SimplePaletteColorOptions>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'successColor'],
  defaultValue: successPaletteColorOptions,
});

export const headerBgPaperColorAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'headerBgColor'],
  defaultValue: '#fff',
});

export const headerBgDefaultColorAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'headerBgDefaultColor'],
  defaultValue: '#f1f3f4',
});

export const textPrimaryColorAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'textPrimaryColor'],
  defaultValue: 'rgba(0, 0, 0, 0.87)',
});
export const textPrimaryColorDarkModeAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'textPrimaryColorDarkMode'],
  defaultValue: '#fff',
});

export const textSecondaryColorAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'textSecondaryColor'],
  defaultValue: 'rgba(0, 0, 0, 0.6)',
});
export const textSecondaryColorDarkModeAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'textSecondaryColorDarkMode'],
  defaultValue: 'rgba(255, 255, 255, 0.7)',
});

export const tooltipBackgroundColorAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'tooltipBackgroundColor'],
  defaultValue: '#444',
});
export const tooltipColorAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'tooltipColor'],
  defaultValue: '#fff',
});

export const tooltipBackgroundColorDarkModeAtom = atom<string>({
  path: [
    'db',
    'IoAppSettings',
    ':appUid',
    'md',
    'tooltipBackgroundColorDarkMode',
  ],
  defaultValue: '#444',
});
export const tooltipColorDarkModeAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'tooltipColorDarkMode'],
  defaultValue: '#fff',
});

export const textDisabledColorAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'textDisabledColor'],
  defaultValue: 'rgba(0, 0, 0, 0.38)',
});
export const textDisabledColorDarkModeAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'textDisabledColorDarkMode'],
  defaultValue: 'rgba(255, 255, 255, 0.5)',
});

export const defaultBackgroundColorAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'defaultBackgroundColor'],
  defaultValue: '#f1f3f4',
});
export const defaultBackgroundColorDarkModeAtom = atom<string>({
  path: [
    'db',
    'IoAppSettings',
    ':appUid',
    'md',
    'defaultBackgroundColorDarkMode',
  ],
  defaultValue: '#303030',
});

export const headerTextColorAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'headerTextColor'],
  defaultValue: 'rgba(0, 0, 0, 0.87)',
});

export const headerBorderColorAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'headerBorderColor'],
  defaultValue: 'rgba(0, 0, 0, 0.12)',
});

export const headerBgPaperColorDarkModeAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'headerBgColorDarkMode'],
  defaultValue: '#121212',
});

export const headerBgDefaultColorDarkModeAtom = atom<string>({
  path: [
    'db',
    'IoAppSettings',
    ':appUid',
    'md',
    'headerBgDefaultColorDarkMode',
  ],
  defaultValue: '#303030',
});

export const headerTextColorDarkModeAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'headerTextColorDarkMode'],
  defaultValue: '#fff',
});

export const headerBorderColorDarkModeAtom = atom<string>({
  path: ['db', 'IoAppSettings', ':appUid', 'md', 'headerBorderColorDarkMode'],
  defaultValue: 'rgba(255, 255, 255, 0.12)',
});

export function useFontFamiliy(appUid: string): string {
  const platform = usePlatform();
  const options = { params: { appUid } };
  let fontFamily =
    useStateXAtomValue(fontFamilyAtom, options) || fontFamilyAtom.defaultValue;
  let invalidFontFamily = '';
  try {
    const ffArray = JSON.parse(`[${fontFamily}]`);
    if (!Array.isArray(ffArray)) {
      invalidFontFamily = fontFamily;
      fontFamily = fontFamilyAtom.defaultValue;
    }
  } catch (e) {
    invalidFontFamily = fontFamily;
    fontFamily = fontFamilyAtom.defaultValue;
  }
  useEffect(() => {
    if (invalidFontFamily) {
      platform.showError(
        `Invalid font family: ${invalidFontFamily}. Must be comma separated quoted strings.`,
      );
    }
  }, [invalidFontFamily, platform]);
  return fontFamily;
}

/**
 * Use this hook to get the current MUI theme.
 * @returns {Theme} MUI Theme
 */
export function useTheme(): Theme {
  const direction = useDirection();
  const darkMode = useStateXAtomValue(darkModeAtom);
  const appUid = useAppID();
  const options = { params: { appUid } };
  const primary =
    useStateXAtomValue(primaryColorAtom, options) ?? primaryPaletteColorOptions;
  const secondary = useStateXAtomValue(secondaryColorAtom, options);
  const warning = useStateXAtomValue(warningColorAtom, options);
  const success = useStateXAtomValue(successColorAtom, options);
  const fontFamily = useFontFamiliy(appUid);
  const fontSize = useStateXAtomValue(fontSizeAtom, options);

  const textPrimaryColor = useStateXAtomValue(textPrimaryColorAtom, options);
  const textPrimaryColorDarkMode = useStateXAtomValue(
    textPrimaryColorDarkModeAtom,
    options,
  );
  const textSecondaryColor = useStateXAtomValue(
    textSecondaryColorAtom,
    options,
  );
  const textSecondaryColorDarkMode = useStateXAtomValue(
    textSecondaryColorDarkModeAtom,
    options,
  );
  const textDisabledColor = useStateXAtomValue(textDisabledColorAtom, options);
  const textDisabledColorDarkMode = useStateXAtomValue(
    textDisabledColorDarkModeAtom,
    options,
  );
  const defaultBackgroundColor = useStateXAtomValue(
    defaultBackgroundColorAtom,
    options,
  );
  const defaultBackgroundColorDarkMode = useStateXAtomValue(
    defaultBackgroundColorDarkModeAtom,
    options,
  );

  return useBaseTheme({
    darkMode,
    defaultBackgroundColor,
    defaultBackgroundColorDarkMode,
    fontFamily,
    fontSize,
    primary,
    secondary,
    success,
    textDisabledColor,
    textDisabledColorDarkMode,
    textPrimaryColor,
    textPrimaryColorDarkMode,
    textSecondaryColor,
    textSecondaryColorDarkMode,
    warning,
    direction,
  });
}

export function useSystemDarkMode(): void {
  const setDarkMode = useStateXAtomSetter(darkModeAtom);
  const user = useUser();
  const appUid = useAppID();
  const userDarkMode = useStateXAtomValue(userDarkModeAtom, {
    params: { userName: user.userName },
  });
  const appDarkMode = useStateXAtomValue(applicationDarkModeAtom, {
    params: { appUid },
  });
  const prefersDarkMode = false;
  useLayoutEffect(() => {
    const mode = appDarkMode !== 'system' ? appDarkMode : userDarkMode;
    setDarkMode(
      mode === 'dark' || (mode === 'system' && prefersDarkMode)
        ? 'dark'
        : 'light',
    );
  }, [appDarkMode, prefersDarkMode, setDarkMode, userDarkMode]);
}

export interface SidebarColors {
  bgColor: string;
  bgDefaultColor: string;
  borderColor: string;
  textColor: string;
}

export function useSidebarColorSettings(appUid: string): SidebarColors {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const bgColor = useStateXAtomValue(
    dark ? headerBgPaperColorDarkModeAtom : headerBgPaperColorAtom,
    {
      params: { appUid },
    },
  );
  const borderColor = useStateXAtomValue(
    dark ? headerBorderColorDarkModeAtom : headerBorderColorAtom,
    {
      params: { appUid },
    },
  );
  const textColor = useStateXAtomValue(
    dark ? headerTextColorDarkModeAtom : headerTextColorAtom,
    {
      params: { appUid },
    },
  );
  const bgDefaultColor = useStateXAtomValue(
    dark ? headerBgDefaultColorDarkModeAtom : headerBgDefaultColorAtom,
    {
      params: { appUid },
    },
  );
  return { bgColor, bgDefaultColor, borderColor, textColor };
}

export function useHeaderSidebarTheme(appUid: string) {
  const theme = useTheme();
  const { bgColor, borderColor, textColor, bgDefaultColor } =
    useSidebarColorSettings(appUid);
  const toptions: ThemeOptions = {
    palette: {
      background: {
        paper: bgColor,
        default: bgDefaultColor,
      },
      text: {
        primary: textColor,
        secondary: textColor,
      },
      divider: borderColor,
      action: {
        hover: borderColor,
        selected: borderColor,
      },
    },
  };

  return createTheme(theme, toptions);
}

export function lightenBorder(color: string, theme: Theme): string {
  const tc = tinycolor(color).darken(10).toHexString();
  const r = tinycolor.readability(theme.palette.background.paper, tc);
  if (
    r < 2 ||
    tinycolor.readability('#fff', tc) < 2 ||
    tinycolor.readability('#000', tc) < 2
  ) {
    return '#aaa';
  }
  return tc;
}
